interface TransactionListProps {
  filterType: string;
  transactions: any[];
  onEdit: (transaction: any) => void;
  onDelete: (id: number) => void;
}

export default function TransactionList({ filterType, transactions, onEdit, onDelete }: TransactionListProps) {
  const filteredTransactions = filterType === 'all' 
    ? transactions 
    : transactions.filter(t => t.type === filterType);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'income':
        return 'ri-arrow-down-circle-line';
      case 'expense':
        return 'ri-arrow-up-circle-line';
      case 'funds':
        return 'ri-funds-line';
      default:
        return 'ri-money-dollar-circle-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'income':
        return 'text-green-600 bg-green-50';
      case 'expense':
        return 'text-red-600 bg-red-50';
      case 'funds':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleDelete = (id: number, description: string) => {
    if (window.confirm(`Are you sure you want to delete "${description}"?`)) {
      onDelete(id);
    }
  };

  return (
    <div className="overflow-x-auto">
      {filteredTransactions.length === 0 ? (
        <div className="text-center py-12">
          <i className="ri-file-list-line text-6xl text-gray-300 w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
          <p className="text-gray-500 text-lg">No transactions found</p>
          <p className="text-gray-400 text-sm mt-2">Add your first transaction to get started</p>
        </div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Category</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Payment Method</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map((transaction) => (
              <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${getTypeColor(transaction.type)}`}>
                    <i className={`${getTypeIcon(transaction.type)} w-4 h-4 flex items-center justify-center`}></i>
                    <span className="text-xs font-medium capitalize">{transaction.type}</span>
                  </div>
                </td>
                <td className="py-4 px-4 text-sm text-gray-700">{transaction.category}</td>
                <td className="py-4 px-4 text-sm text-gray-900 font-medium">{transaction.description}</td>
                <td className="py-4 px-4 text-sm text-gray-600">{transaction.date}</td>
                <td className="py-4 px-4 text-sm text-gray-600">{transaction.paymentMethod}</td>
                <td className="py-4 px-4 text-right">
                  <span className={`text-sm font-semibold ${
                    transaction.type === 'income' || transaction.type === 'funds' 
                      ? 'text-green-600' 
                      : 'text-red-600'
                  }`}>
                    {transaction.type === 'expense' ? '-' : '+'}₹{transaction.amount.toLocaleString()}
                  </span>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    <button 
                      onClick={() => onEdit(transaction)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      title="Edit transaction"
                    >
                      <i className="ri-edit-line text-gray-600 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                    <button 
                      onClick={() => handleDelete(transaction.id, transaction.description)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      title="Delete transaction"
                    >
                      <i className="ri-delete-bin-line text-red-600 w-4 h-4 flex items-center justify-center"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
